// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\Organization\ApiOrganization.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { AgencyCode, AgencyCodeData, AgencyCodeOptions } from 'data/models/AgencyCode';
import { Berth, BerthData, BerthOptions } from 'data/models/Berth';
import { BerthAmenityType, BerthAmenityTypeData, BerthAmenityTypeOptions } from 'data/models/BerthAmenityType';
import { Bill, BillData, BillOptions } from 'data/models/Bill';
import { BillingSystem, BillingSystemData, BillingSystemOptions } from 'data/models/BillingSystem';
import { CargoActivityType, CargoActivityTypeData, CargoActivityTypeOptions } from 'data/models/CargoActivityType';
import { CargoCategory, CargoCategoryData, CargoCategoryOptions } from 'data/models/CargoCategory';
import { CoordinationProvider, CoordinationProviderData, CoordinationProviderOptions } from 'data/models/CoordinationProvider';
import { Domain, DomainData, DomainOptions } from 'data/models/Domain';
import { Image, ImageData, ImageOptions } from 'data/models/Image';
import { PCUser, PCUserData, PCUserOptions } from 'data/models/PCUser';
import { PhoneNumber, PhoneNumberData, PhoneNumberOptions } from 'data/models/PhoneNumber';
import { PilotAgency, PilotAgencyData, PilotAgencyOptions } from 'data/models/PilotAgency';
import { PostalAddress, PostalAddressData, PostalAddressOptions } from 'data/models/PostalAddress';
import { Share, ShareData, ShareOptions } from 'data/models/Share';
import { TopicCardTemplate, TopicCardTemplateData, TopicCardTemplateOptions } from 'data/models/TopicCardTemplate';
import { TradeRoute, TradeRouteData, TradeRouteOptions } from 'data/models/TradeRoute';
import { UITheme, UIThemeData, UIThemeOptions } from 'data/models/UITheme';

// OrganizationData is the class that Organization serializes to/from
export interface OrganizationData {
    Id : number;
    Url : string | null;
	
    AgencyCodesAsCodedOrganization : AgencyCodeData[] | null;
    BerthAmenityTypes : BerthAmenityTypeData[] | null;
    BerthAmenityTypesUrl : string | null;
    BillingSystems : BillingSystemData[] | null;
    BillsAsCustomer : BillData[] | null;
    CargoActivityTypes : CargoActivityTypeData[] | null;
    CargoActivityTypesUrl : string | null;
    CargoCategories : CargoCategoryData[] | null;
    CargoCategoriesUrl : string | null;
    CoordinationsProvided : CoordinationProviderData[] | null;
    Domain : DomainData | null;
    DomainId : number;
    ManagedBerths : BerthData[] | null;
    PhoneNumbers : PhoneNumberData[] | null;
    PhoneNumbersUrl : string | null;
    PilotAgency : PilotAgencyData | null;
    PostalAddresses : PostalAddressData[] | null;
    PostalAddressesUrl : string | null;
    Shares : ShareData[] | null;
    TopicCardTemplates : TopicCardTemplateData[] | null;
    TopicCardTemplatesUrl : string | null;
    TradeRoutes : TradeRouteData[] | null;
    TradeRoutesUrl : string | null;
    UITheme : UIThemeData | null;
    UIThemeId : number | null;
    Users : PCUserData[] | null;
	
    Active : boolean;
    FinalReminderWindow : number | null;
    FirstReminderWindow : number | null;
    Guid : /* GUID */ string;
    Image : ImageData;
    Name : string;
}

// OrganizationOptions is the class which may be passed to the Organization constructor to define optional initial data values
export interface OrganizationOptions {
    Id : number;
    Url? : string | null;
	
    AgencyCodesAsCodedOrganization? : AgencyCodeOptions[] | null;
    BerthAmenityTypes? : BerthAmenityTypeOptions[] | null;
    BerthAmenityTypesUrl? : string | null;
    BillingSystems? : BillingSystemOptions[] | null;
    BillsAsCustomer? : BillOptions[] | null;
    CargoActivityTypes? : CargoActivityTypeOptions[] | null;
    CargoActivityTypesUrl? : string | null;
    CargoCategories? : CargoCategoryOptions[] | null;
    CargoCategoriesUrl? : string | null;
    CoordinationsProvided? : CoordinationProviderOptions[] | null;
    Domain? : DomainOptions | null;
    DomainId? : number;
    ManagedBerths? : BerthOptions[] | null;
    PhoneNumbers? : PhoneNumberOptions[] | null;
    PhoneNumbersUrl? : string | null;
    PilotAgency? : PilotAgencyOptions | null;
    PostalAddresses? : PostalAddressOptions[] | null;
    PostalAddressesUrl? : string | null;
    Shares? : ShareOptions[] | null;
    TopicCardTemplates? : TopicCardTemplateOptions[] | null;
    TopicCardTemplatesUrl? : string | null;
    TradeRoutes? : TradeRouteOptions[] | null;
    TradeRoutesUrl? : string | null;
    UITheme? : UIThemeOptions | null;
    UIThemeId? : number | null;
    Users? : PCUserOptions[] | null;
	
    Active? : boolean;
    FinalReminderWindow? : number | null;
    FirstReminderWindow? : number | null;
    Guid? : /* GUID */ string;
    Image? : ImageOptions;
    Name? : string;
}

export class Organization {
    public Id : DataSignal<number>;
    public Url : DataSignal<string | null>;
	
    public AgencyCodesAsCodedOrganization : SDataArray<AgencyCode>;
    public BerthAmenityTypes : SDataArray<BerthAmenityType>;
    public BerthAmenityTypesUrl : DataSignal<string | null>;
    public BillingSystems : SDataArray<BillingSystem>;
    public BillsAsCustomer : SDataArray<Bill>;
    public CargoActivityTypes : SDataArray<CargoActivityType>;
    public CargoActivityTypesUrl : DataSignal<string | null>;
    public CargoCategories : SDataArray<CargoCategory>;
    public CargoCategoriesUrl : DataSignal<string | null>;
    public CoordinationsProvided : SDataArray<CoordinationProvider>;
    public Domain : DataSignal<Domain | null>;
    public DomainId : DataSignal<number>;
    public ManagedBerths : SDataArray<Berth>;
    public PhoneNumbers : SDataArray<PhoneNumber>;
    public PhoneNumbersUrl : DataSignal<string | null>;
    public PilotAgency : DataSignal<PilotAgency | null>;
    public PostalAddresses : SDataArray<PostalAddress>;
    public PostalAddressesUrl : DataSignal<string | null>;
    public Shares : SDataArray<Share>;
    public TopicCardTemplates : SDataArray<TopicCardTemplate>;
    public TopicCardTemplatesUrl : DataSignal<string | null>;
    public TradeRoutes : SDataArray<TradeRoute>;
    public TradeRoutesUrl : DataSignal<string | null>;
    public UITheme : DataSignal<UITheme | null>;
    public UIThemeId : DataSignal<number | null>;
    public Users : SDataArray<PCUser>;
	
    public Active : DataSignal<boolean>;
    public FinalReminderWindow : DataSignal<number | null>;
    public FirstReminderWindow : DataSignal<number | null>;
    public Guid : DataSignal<string>;
    public Image : Image;
    public Name : DataSignal<string>;

    constructor();
    constructor(data : OrganizationOptions);
    constructor(data: OrganizationOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
        this.Url = jsonableObservable(S.value<string | null>(data && data.Url !== undefined ? data.Url : null));
	    
        this.AgencyCodesAsCodedOrganization = SArray<AgencyCode>([]);
        this.BerthAmenityTypes = SArray<BerthAmenityType>([]);
        this.BerthAmenityTypesUrl = jsonableObservable(S.value<string | null>(data && data.BerthAmenityTypesUrl !== undefined ? data.BerthAmenityTypesUrl : null));
        this.BillingSystems = SArray<BillingSystem>([]);
        this.BillsAsCustomer = SArray<Bill>([]);
        this.CargoActivityTypes = SArray<CargoActivityType>([]);
        this.CargoActivityTypesUrl = jsonableObservable(S.value<string | null>(data && data.CargoActivityTypesUrl !== undefined ? data.CargoActivityTypesUrl : null));
        this.CargoCategories = SArray<CargoCategory>([]);
        this.CargoCategoriesUrl = jsonableObservable(S.value<string | null>(data && data.CargoCategoriesUrl !== undefined ? data.CargoCategoriesUrl : null));
        this.CoordinationsProvided = SArray<CoordinationProvider>([]);
        this.Domain = S.value<Domain | null>(null);
        this.DomainId = jsonableObservable(S.value<number>(data && data.DomainId !== undefined ? data.DomainId : 0));
        this.ManagedBerths = SArray<Berth>([]);
        this.PhoneNumbers = SArray<PhoneNumber>([]);
        this.PhoneNumbersUrl = jsonableObservable(S.value<string | null>(data && data.PhoneNumbersUrl !== undefined ? data.PhoneNumbersUrl : null));
        this.PilotAgency = S.value<PilotAgency | null>(null);
        this.PostalAddresses = SArray<PostalAddress>([]);
        this.PostalAddressesUrl = jsonableObservable(S.value<string | null>(data && data.PostalAddressesUrl !== undefined ? data.PostalAddressesUrl : null));
        this.Shares = SArray<Share>([]);
        this.TopicCardTemplates = SArray<TopicCardTemplate>([]);
        this.TopicCardTemplatesUrl = jsonableObservable(S.value<string | null>(data && data.TopicCardTemplatesUrl !== undefined ? data.TopicCardTemplatesUrl : null));
        this.TradeRoutes = SArray<TradeRoute>([]);
        this.TradeRoutesUrl = jsonableObservable(S.value<string | null>(data && data.TradeRoutesUrl !== undefined ? data.TradeRoutesUrl : null));
        this.UITheme = S.value<UITheme | null>(null);
        this.UIThemeId = jsonableObservable(S.value<number | null>(data && data.UIThemeId !== undefined ? data.UIThemeId : null));
        this.Users = SArray<PCUser>([]);
	    
        this.Active = jsonableObservable(S.value<boolean>(data && data.Active !== undefined ? data.Active : false));
        this.FinalReminderWindow = jsonableObservable(S.value<number | null>(data && data.FinalReminderWindow !== undefined ? data.FinalReminderWindow : null));
        this.FirstReminderWindow = jsonableObservable(S.value<number | null>(data && data.FirstReminderWindow !== undefined ? data.FirstReminderWindow : null));
        this.Guid = jsonableObservable(S.value<string>(data && data.Guid !== undefined ? data.Guid : '00000000-0000-0000-0000-000000000000'));
        this.Image = data && data.Image !== undefined ? new Image(data.Image) : new Image();
        this.Name = jsonableObservable(S.value<string>(data && data.Name !== undefined ? data.Name : ''));
    }

    static key(obj : Organization) {
        return obj.Id();
    }
    
    static dataKey(obj : OrganizationOptions) {
        return obj.Id;
    }

    static update(obj : Organization, data : OrganizationOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
            if (data.Url !== undefined) obj.Url(data.Url);
	        
            if (data.BerthAmenityTypesUrl !== undefined) obj.BerthAmenityTypesUrl(data.BerthAmenityTypesUrl);
            if (data.CargoActivityTypesUrl !== undefined) obj.CargoActivityTypesUrl(data.CargoActivityTypesUrl);
            if (data.CargoCategoriesUrl !== undefined) obj.CargoCategoriesUrl(data.CargoCategoriesUrl);
            if (data.DomainId !== undefined) obj.DomainId(data.DomainId);
            if (data.PhoneNumbersUrl !== undefined) obj.PhoneNumbersUrl(data.PhoneNumbersUrl);
            if (data.PostalAddressesUrl !== undefined) obj.PostalAddressesUrl(data.PostalAddressesUrl);
            if (data.TopicCardTemplatesUrl !== undefined) obj.TopicCardTemplatesUrl(data.TopicCardTemplatesUrl);
            if (data.TradeRoutesUrl !== undefined) obj.TradeRoutesUrl(data.TradeRoutesUrl);
            if (data.UIThemeId !== undefined) obj.UIThemeId(data.UIThemeId);
	        
            if (data.Active !== undefined) obj.Active(data.Active);
            if (data.FinalReminderWindow !== undefined) obj.FinalReminderWindow(data.FinalReminderWindow);
            if (data.FirstReminderWindow !== undefined) obj.FirstReminderWindow(data.FirstReminderWindow);
            if (data.Guid !== undefined) obj.Guid(data.Guid);
            if (data.Image !== undefined) Image.update(obj.Image, data.Image);
            if (data.Name !== undefined) obj.Name(data.Name);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/Organization.ts